import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Button, TextField,
  Stack, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Checkbox, Alert, Box,
} from '@mui/material';
import { AssignmentReturnOutlined } from '@mui/icons-material';
import { StyledCellHeader } from '../../../components/StyledCellHeader';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

/**
 * Crea una nueva transferencia (DEV-…) en sentido inverso a `transferencia`.
 * La nueva transferencia queda ligada via transferencia_original_id.
 * NO muta la transferencia original — mantiene inmutabilidad del audit trail.
 */
export default function CrearDevolucionDialog({ open, onClose, transferencia, onDone }) {
  const [seleccion, setSeleccion] = useState({}); // { id_producto_destino: cantidad }
  const [transportista, setTransportista] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Sólo items que tuvieron recepción efectiva en destino pueden devolverse.
  const itemsDevolvibles = (transferencia?.items || []).filter(
    it => it.id_producto_destino && it.cantidad_recibida > 0,
  );

  useEffect(() => {
    if (!open) return;
    setSeleccion({});
    setTransportista('');
    setObservaciones('');
    setError('');
  }, [open]);

  const toggleItem = (it) => {
    setSeleccion(prev => {
      const next = { ...prev };
      if (next[it.id_producto_destino]) delete next[it.id_producto_destino];
      else next[it.id_producto_destino] = it.cantidad_recibida;
      return next;
    });
  };

  const setCantidad = (it, value) => {
    const n = Math.max(0, Math.min(parseInt(value, 10) || 0, it.cantidad_recibida));
    setSeleccion(prev => ({ ...prev, [it.id_producto_destino]: n }));
  };

  const itemsParaEnviar = Object.entries(seleccion)
    .filter(([, qty]) => qty > 0)
    .map(([id, cantidad_enviada]) => ({ id_producto_origen: id, cantidad_enviada }));

  const handleConfirm = async () => {
    if (itemsParaEnviar.length === 0) { setError('Seleccioná al menos un item para devolver'); return; }
    setSaving(true); setError('');
    try {
      const body = {
        idcompany: localStorage.getItem('idcompany'),
        // invertir dirección
        id_almacen_origen: transferencia.id_almacen_destino,
        id_almacen_destino: transferencia.id_almacen_origen,
        transportista: transportista.trim(),
        observaciones_envio: observaciones.trim(),
        items: itemsParaEnviar,
        transferencia_original_id: transferencia.id,
        created_by: localStorage.getItem('id'),
      };
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Error al crear devolución');
      onDone?.(result);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>
        Crear devolución de {transferencia?.codigo}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Se creará una nueva transferencia en estado <strong>pendiente</strong> desde <strong>{transferencia?.almacen_destino_nombre}</strong> hacia <strong>{transferencia?.almacen_origen_nombre}</strong>. Podés revisarla y enviarla desde su detalle.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {itemsDevolvibles.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>No hay items devolvibles (ningún producto fue recibido efectivamente en destino).</Alert>
        )}

        {itemsDevolvibles.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledCellHeader sx={{ width: 50 }} />
                  <StyledCellHeader>Producto</StyledCellHeader>
                  <StyledCellHeader sx={{ textAlign: 'center' }}>Recibido</StyledCellHeader>
                  <StyledCellHeader sx={{ textAlign: 'center' }}>A devolver</StyledCellHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemsDevolvibles.map((it) => {
                  const marcado = seleccion[it.id_producto_destino] !== undefined;
                  return (
                    <TableRow key={it.id} hover>
                      <TableCell>
                        <Checkbox size="small" checked={marcado} onChange={() => toggleItem(it)} />
                      </TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>{it.producto_nombre}</Typography></TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{it.cantidad_recibida}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <TextField
                          size="small" type="number"
                          value={seleccion[it.id_producto_destino] ?? ''}
                          onChange={(e) => setCantidad(it, e.target.value)}
                          disabled={!marcado}
                          sx={{ width: 80, '& .MuiInputBase-input': { textAlign: 'center', fontWeight: 600 } }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Stack spacing={2}>
          <TextField
            size="small" fullWidth label="Transportista" placeholder="Nombre o empresa de transporte"
            value={transportista} onChange={(e) => setTransportista(e.target.value)}
          />
          <TextField
            size="small" fullWidth multiline minRows={2}
            label="Motivo de devolución"
            value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained" color="primarybutton" startIcon={<AssignmentReturnOutlined />}
          onClick={handleConfirm} disabled={saving || itemsDevolvibles.length === 0}
          sx={{ fontWeight: 700 }}
        >
          {saving ? 'Creando…' : 'Crear devolución'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
